import { create } from 'zustand';
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from 'react';
import { toast } from "sonner";
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface InventoryState {
  isInventoryActive: boolean;
  setIsInventoryActive: (active: boolean) => Promise<void>;
}

interface InventoryStateRow {
  id: string;
  owner_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const useInventoryStateStore = create<InventoryState>((set) => ({
  isInventoryActive: false,
  setIsInventoryActive: async (active) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('inventory_states')
        .upsert(
          {
            owner_id: user.id,
            is_active: active,
            updated_at: new Date().toISOString()
          },
          { 
            onConflict: 'owner_id'
          }
        );

      if (error) {
        console.error('Error updating inventory state:', error);
        throw error;
      }
      
      set({ isInventoryActive: active });
    } catch (error) {
      console.error('Error updating inventory state:', error);
      toast.error("Erreur lors de la mise à jour de l'état de l'inventaire");
    }
  }
}));

export const useInventoryState = () => {
  const state = useInventoryStateStore();

  useEffect(() => {
    const initializeState = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get initial state
        const { data, error } = await supabase
          .from('inventory_states')
          .select('*')
          .eq('owner_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          useInventoryStateStore.setState({ isInventoryActive: data.is_active });
        }

        // Subscribe to real-time changes
        const channel = supabase.channel('inventory_state_changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'inventory_states',
              filter: `owner_id=eq.${user.id}`
            },
            (payload: RealtimePostgresChangesPayload<InventoryStateRow>) => {
              if ('new' in payload && payload.new && 'is_active' in payload.new) {
                useInventoryStateStore.setState({
                  isInventoryActive: payload.new.is_active
                });
              }
            }
          )
          .subscribe();

        return () => {
          channel.unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing inventory state:', error);
        toast.error("Erreur lors de l'initialisation de l'état de l'inventaire");
      }
    };

    initializeState();
  }, []);

  return state;
};